// Today's paper's board: what a player sends, checked, and the score worked out again from the
// decisions alone. Pure: the edge scores every submission itself and never takes a page's word.

import {
  DECK_SIZE,
  LEVERAGES,
  MARGIN_CENTS,
  POINTS_PER_MINUTE,
  REPLAY_MINUTES,
  START_CENTS,
  dailyDeck,
  settle,
  type Decision,
  type Round,
} from "./rounds.ts";

/** What the page sends once today's paper is done: who, under what name, and each round's call. */
export type Submission = { player: string; name: string; decisions: Decision[] };

/** One line of the board. The bankroll is cents, as a decimal string (BigInt does not survive JSON). */
export type BoardRow = { name: string; bankroll: string; marks: string };

/** A day's board: the top, how many played, and the asking player's line. `rank` is 1-based;
 * `beat` counts the players with a strictly lower bankroll. */
export type Board = { day: number; of: number; top: BoardRow[]; you: (BoardRow & { rank: number; beat: number }) | null };

/** A browser's random id. */
export const PLAYER_ID = /^[A-Za-z0-9-]{8,64}$/;

/** Letters (any script), digits, spaces, `.`, `_` and `-`; 16 at most. */
const NAME = /^[\p{L}\p{Nd} ._-]{1,16}$/u;

/** The last path point a close can name: the end of the hour. */
const LAST_POINT = REPLAY_MINUTES * POINTS_PER_MINUTE;

/** A name as the board shows it, or null when it is not one. */
export function cleanName(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const name = raw.normalize("NFC").trim();
  return NAME.test(name) ? name : null;
}

function parseDecision(raw: unknown, n: number): Decision | string {
  if (typeof raw !== "object" || raw === null) return `decision ${n} must be an object`;
  const { side, leverage, closedAt } = raw as Record<string, unknown>;
  if (side !== "long" && side !== "short" && side !== null) return `decision ${n}: side must be long, short or null`;
  if (typeof leverage !== "number" || !(LEVERAGES as readonly number[]).includes(leverage)) {
    return `decision ${n}: leverage must be one of ${LEVERAGES.join(", ")}`;
  }
  if (closedAt !== null && !(typeof closedAt === "number" && Number.isInteger(closedAt) && closedAt >= 0 && closedAt <= LAST_POINT)) {
    return `decision ${n}: closedAt must be null or a whole number from 0 to ${LAST_POINT}`;
  }
  return { side, leverage, closedAt };
}

/** A submission, checked and stripped to its fields, or what is wrong with it. */
export function parseSubmission(body: unknown): Submission | string {
  if (typeof body !== "object" || body === null) return "the body must be a JSON object";
  const { player, name, decisions } = body as Record<string, unknown>;
  if (typeof player !== "string" || !PLAYER_ID.test(player)) return "player must be 8 to 64 letters, digits or dashes";
  const clean = cleanName(name);
  if (clean === null) return "name must be 1 to 16 letters, digits, spaces, dots, underscores or dashes";
  if (!Array.isArray(decisions) || decisions.length !== DECK_SIZE) return `decisions must hold exactly ${DECK_SIZE} rounds`;
  const parsed: Decision[] = [];
  for (const [i, raw] of decisions.entries()) {
    const decision = parseDecision(raw, i + 1);
    if (typeof decision === "string") return decision;
    parsed.push(decision);
  }
  return { player, name: clean, decisions: parsed };
}

const SAT_OUT: Decision = { side: null, leverage: LEVERAGES[0], closedAt: null };

/** The day's bankroll and marks from the decisions alone, settled exactly as the page settles
 * them. ⬜ sat out, 💀 busted, 🟩 a win, 🟥 anything else. */
export function scoreDeck(rounds: Round[], day: number, decisions: Decision[]): { bankroll: bigint; marks: string } {
  let bankroll = START_CENTS;
  let marks = "";
  for (const [i, round] of dailyDeck(rounds, day).entries()) {
    const decision = decisions[i] ?? SAT_OUT;
    const { pnl, busted } = settle(round, decision, MARGIN_CENTS);
    bankroll += pnl;
    marks += !decision.side ? "⬜" : busted ? "💀" : pnl > 0n ? "🟩" : "🟥";
  }
  return { bankroll, marks };
}
